import { Box, Typography } from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableContainer from '@mui/material/TableContainer';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Pagination from '../../components/Pagination/Pagination';
import { LANG_TEXT } from '../../constants/language';
import { useAuthContext } from '../../context/AuthContext/AuthContext';
import { useOrderContext } from '../../context/OrderContext';
import { useOrderStadiumContext } from '../../context/OrderStadiumContext';
import { useSettingsContext } from '../../context/SettingsContext';
import usePermission from '../../hooks/usePermission';
import { usePreorderEnabled } from '../../hooks/usePreorderEnabled';
import { DamagedItemStatus, OrderItemStatus, OrderSummaryData } from '../../interfaces/index.types';
import { getEndOfDay, getStartOfDay } from '../../utils/handleDateTime';
import { parseOrderList } from '../../utils/orderHistory.utils';
import { OrderType } from '../Dashboard/order.types';
import DatePickerCustom from './components/DatePicker/DatePicker';
import DownloadCsv from './components/DownloadCsv/DownloadCsv';
import Loader from './components/Loader/Loader';
import TableBodyCustom from './components/TableBody/TableBody';
import TableHeadCustom from './components/TableHead/TableHead';
import TableSummary from './components/TableSummary/TableSummary';
import { IOrderHistory } from './orderHistory.types';
import { useStyles } from './styles';

interface OrderHistoryProps {
  orderType?: OrderType;
}

export default function PreOrderList({ orderType }: OrderHistoryProps) {
  const classes = useStyles();
  const { t } = useTranslation();

  const { userInfo } = useAuthContext();
  const { showForSuperAdmin, showForAdmin } = usePermission();
  const orderContextOfStadium = useOrderStadiumContext();
  const orderContextOfRestaurant = useOrderContext();
  const { currentSetting } = useSettingsContext();
  const { enabledNewVersion } = usePreorderEnabled();

  const { preOrder, loading, seatDataMap, riderMap } = useMemo(() => {
    if (showForSuperAdmin) {
      return {
        preOrder: orderContextOfStadium.preOrder,
        loading: orderContextOfStadium.loading,
        seatDataMap: orderContextOfStadium.seatDataMap,
        riderMap: orderContextOfStadium.riderMap,
      };
    } else {
      return {
        preOrder: orderContextOfRestaurant.futurePreOrders,
        loading: orderContextOfRestaurant.loading,
        seatDataMap: orderContextOfRestaurant.seatDataMap,
        riderMap: orderContextOfRestaurant.riderMap,
      };
    }
  }, [
    showForSuperAdmin,
    orderContextOfStadium.preOrder,
    orderContextOfStadium.loading,
    orderContextOfStadium.seatDataMap,
    orderContextOfStadium.riderMap,
    orderContextOfRestaurant.futurePreOrders,
    orderContextOfRestaurant.loading,
    orderContextOfRestaurant.seatDataMap,
    orderContextOfRestaurant.riderMap,
  ]);

  const [page, setPage] = useState<number>(0);
  const rowsPerPage = 10;
  const [dateFilter, setDateFilter] = useState<{ start: Date | null; end: Date | null }>({ start: getStartOfDay(new Date()), end: getStartOfDay(new Date()) });
  const [selectedFoodTrucks, setSelectedFoodTrucks] = useState<string[]>([]);
  const prefix = '_DAMAGED';

  const ORDER_TYPE_TITLES: Record<OrderType, string> = {
    [OrderType.REGULAR_ORDERS]: t(LANG_TEXT.REGULAR_ORDERS),
    [OrderType.SPECIAL_ORDERS]: t(LANG_TEXT.SPECIAL_ORDERS),
    [OrderType.REGULAR_PRE_ORDERS]: t(LANG_TEXT.REGULAR_PRE_ORDERS),
    [OrderType.SPECIAL_PRE_ORDERS]: t(LANG_TEXT.SPECIAL_PRE_ORDERS),
  };

  const pageTitle = orderType ? ORDER_TYPE_TITLES[orderType] : t(LANG_TEXT.PRE_ORDER_LIST);

  const handleDateSelect = (start: Date | null, end: Date | null) => {
    setDateFilter({ start, end });
    setPage(0);
  };

  const onPageChange = (newPage: number) => {
    setPage(newPage);
  };

  const [displayedOrders, setDisplayedOrders] = useState<IOrderHistory[]>([]);

  useEffect(() => {
    const loadOrders = async () => {
      const parsedOrders = await parseOrderList(preOrder, userInfo, currentSetting, seatDataMap, t, riderMap || {}, enabledNewVersion);

      const today = new Date();
      const startDate = getStartOfDay(today);

      const futureOrders = parsedOrders.filter((order) => {
        if (order.requested_delivery_time) {
          const deliveryDate = new Date(order.requested_delivery_time.seconds * 1000);
          return deliveryDate >= startDate;
        }
        return false;
      });

      const sortedFutureOrders = futureOrders.sort((a, b) => {
        const dateA = a.requested_delivery_time ? a.requested_delivery_time.seconds : 0;
        const dateB = b.requested_delivery_time ? b.requested_delivery_time.seconds : 0;
        return dateA - dateB;
      });

      // Filter by date range if provided
      let filteredByDate = sortedFutureOrders;
      if (dateFilter.start && dateFilter.end) {
        filteredByDate = sortedFutureOrders.filter((order) => {
          if (!order.requested_delivery_time) return false;

          const time = new Date(order.requested_delivery_time.seconds * 1000);
          const dateMatch = time >= getStartOfDay(dateFilter.start!) && time <= getEndOfDay(dateFilter.end!);
          return dateMatch && (!orderType || order.order_type === orderType);
        });
      } else if (orderType) {
        filteredByDate = sortedFutureOrders.filter((order) => !orderType || order.order_type === orderType);
      }
      // Filter by food trucks
      if (selectedFoodTrucks.length === 0) {
        setDisplayedOrders(filteredByDate);
      } else {
        setDisplayedOrders(
          filteredByDate.filter((order) => {
            const singleOrder = order.restaurant_id && selectedFoodTrucks.includes(order.restaurant_id);
            const multipleOrder = order.multi_orders && order.multi_orders.some((item) => selectedFoodTrucks.includes(item.restaurant_id));
            return singleOrder || multipleOrder;
          })
        );
      }
    };

    loadOrders();
  }, [preOrder, userInfo, currentSetting, seatDataMap, t, riderMap, dateFilter, orderType, selectedFoodTrucks]);

  const handleFoodTruckFilterChange = (selected: string[]) => {
    setSelectedFoodTrucks(selected);
    setPage(0);
  };

  const orderSummary = useMemo<{ [key: string]: OrderSummaryData }>(() => {
    const calculateOrderStats = (orders: IOrderHistory[]): OrderSummaryData => {
      const totalOrders = new Set(orders.map((order) => order.id)).size;

      const totalDeliveryFee = orders.filter((order) => order.item_food_id === t(LANG_TEXT.DELIVERY_FEE_ETC)).reduce((sum, order) => sum + (order.item_price ?? 0), 0);

      const totalOrderAmount = orders.reduce((sum, order) => sum + (order.item_price ?? 0) * (order.item_quantity ?? 0), 0);
      const totalDamagedFee = orders
        .filter((order) => order.id.includes(prefix))
        .reduce((sum, order) => sum + (order.item_price ?? 0) * (order.item_quantity ?? 0), 0);

      const totalFoodRevenue = totalOrderAmount - totalDeliveryFee;
      const totalWithServiceCharge = totalFoodRevenue * 0.1;
      const totalBenefit = totalDeliveryFee + totalWithServiceCharge;

      const uniqueOrderIds = Array.from(new Set(orders.map((order) => order.id)));

      const cancelledOrderIds = new Set(orders.filter((order) => order.order_status === OrderItemStatus.CANCELLED).map((order) => order.id));
      const totalCancelledOrder = uniqueOrderIds.filter((id) => cancelledOrderIds.has(id)).length;

      const deliveredOrderIds = new Set(
        orders
          .filter((order) => order.order_status !== OrderItemStatus.CANCELLED && order.order_status !== DamagedItemStatus.DAMAGED_CANCELLED && !order.id.includes(prefix))
          .map((order) => order.id)
      );
      const totalDeliveredOrder = uniqueOrderIds.filter((id) => deliveredOrderIds.has(id)).length;
      const totalDamagedOrder = uniqueOrderIds.filter((id) => id.includes(prefix)).length;

      return {
        totalOrders,
        totalDeliveryFee,
        totalOrderAmount,
        totalFoodRevenue,
        totalDamagedFee,
        totalWithServiceCharge,
        totalBenefit,
        totalDeliveredOrder,
        totalDamagedOrder,
        totalCancelledOrder,
      };
    };

    if (orderType) {
      return {
        [orderType]: calculateOrderStats(displayedOrders),
      };
    }

    const regularPre = displayedOrders.filter((o) => o.order_type === OrderType.REGULAR_PRE_ORDERS);
    const specialPre = displayedOrders.filter((o) => o.order_type === OrderType.SPECIAL_PRE_ORDERS);

    return {
      regular_pre_order: calculateOrderStats(regularPre),
      special_pre_order: calculateOrderStats(specialPre),
    };
  }, [displayedOrders, t]);

  useEffect(() => {
    setSelectedFoodTrucks([]);
  }, [preOrder, orderType]);

  return (
    <Box
      component="main"
      className={classes.Dashboard}
      sx={{
        backgroundColor: (theme) => (theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900]),
      }}
    >
      <Box className={classes.BoxTitle}>
        <Typography fontSize={24} lineHeight={'32px'} fontWeight={500} color={'var(--Gray-900, #101828)'}>
          {pageTitle}
        </Typography>
        {!loading && (
          <Box className={classes.BoxDownload}>
            <DatePickerCustom onSelect={handleDateSelect} minDate={new Date()} />
            <DownloadCsv
              csvData={displayedOrders}
              seatDataMap={seatDataMap}
              showForSuperAdmin={showForSuperAdmin}
              showForAdmin={showForAdmin}
              summaryData={orderSummary}
              routeType="pre_orders"
            />
          </Box>
        )}
      </Box>
      {loading ? (
        <Loader />
      ) : (
        <Paper className={classes.Table}>
          <TableContainer className={classes.Scrollbar}>
            <Table stickyHeader aria-label="sticky table" sx={{ borderCollapse: 'collapse' }}>
              <TableHeadCustom style={classes.Table} showForSuperAdmin={showForSuperAdmin} />
              <TableBodyCustom data={displayedOrders} rowsPerPage={rowsPerPage} page={page} showForSuperAdmin={showForSuperAdmin} />
            </Table>
          </TableContainer>
          <Box className={classes.BoxSummary}>
            <TableSummary
              style={classes.Table}
              tableCell={classes.TableCell}
              showForSuperAdmin={showForSuperAdmin}
              data={orderSummary}
              routeType="pre_orders"
              selectedFoodTrucks={selectedFoodTrucks}
              onFilterChange={handleFoodTruckFilterChange}
            />
          </Box>
          <Pagination data={displayedOrders} rowsPerPage={rowsPerPage} onPageChange={onPageChange} />
        </Paper>
      )}
    </Box>
  );
}
